/**
 * §7.7 — notifications are shared state, not a header widget's private data.
 * Action-required items ALSO render as a persistent banner inside the relevant
 * screen, so the list has to be reachable from anywhere in the tree.
 */

import { createContext, useCallback, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { apiFetch } from '../api/client';
import { routes } from '../api/routes';
import { useApi } from './useApi';
import { useSession } from '../session/SessionContext';
import type { Notification, Page } from '../api/types';

interface NotificationsValue {
  items: Notification[];
  unread: number;
  unreadActionRequired: number;
  loading: boolean;
  reload: () => void;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  /** Unread action-required items scoped to one bot or rental, for the header banner. */
  actionRequiredFor: (scope: { botId?: string; rentalId?: string }) => Notification[];
}

const NotificationsContext = createContext<NotificationsValue>({
  items: [],
  unread: 0,
  unreadActionRequired: 0,
  loading: false,
  reload: () => {},
  markRead: async () => {},
  markAllRead: async () => {},
  actionRequiredFor: () => [],
});

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { authenticated } = useSession();
  const { data, loading, reload } = useApi<Page<Notification>>(
    authenticated ? routes.me.notifications : null,
    { pollMs: 60_000, query: { limit: 50 } },
  );

  const items = useMemo(() => data?.data ?? [], [data]);

  // §10.4 marks by id list, not one path per notification and no bulk verb:
  // action-required items do not auto-dismiss, so clearing them is always an
  // explicit act naming exactly what is being cleared (§7.7).
  const markRead = useCallback(
    async (id: string) => {
      await apiFetch(routes.me.notificationsRead, { method: 'POST', body: { ids: [id] } });
      reload();
    },
    [reload],
  );

  const markAllRead = useCallback(async () => {
    const ids = items.filter((n) => !n.read_at).map((n) => n.id);
    if (ids.length === 0) return;
    await apiFetch(routes.me.notificationsRead, { method: 'POST', body: { ids } });
    reload();
  }, [items, reload]);

  const value = useMemo<NotificationsValue>(() => {
    const unreadItems = items.filter((n) => !n.read_at);
    return {
      items,
      unread: unreadItems.length,
      unreadActionRequired: unreadItems.filter((n) => n.priority === 'action_required').length,
      loading,
      reload,
      markRead,
      markAllRead,
      actionRequiredFor: ({ botId, rentalId }) =>
        unreadItems.filter(
          (n) =>
            n.priority === 'action_required' &&
            ((botId && n.bot_id === botId) || (rentalId && n.rental_id === rentalId)),
        ),
    };
  }, [items, loading, reload, markRead, markAllRead]);

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export function useNotifications(): NotificationsValue {
  return useContext(NotificationsContext);
}
